import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { motion } from 'framer-motion';

import './App.css';
import G from './G';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const imageVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const buttonVariants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2
    }
  },
  tap: {
    scale: 0.95
  }
};

// Component to handle instant redirects for numbers 0-7
function RedirectComponent() {
  const { number } = useParams();
  
  useEffect(() => {
    if (number && parseInt(number) >= 0 && parseInt(number) <= 7) {
      // Instant redirect to bouncy URL
      window.location.href = `https://bouncy.ai/nelliesweetie${number}`;
    }
  }, [number]);
  
  // Return blank screen during redirect
  return (
    <div style={{ 
      height: '100vh', 
      background: '#fff' 
    }}>
    </div>
  );
}

// Component to handle dynamic URLs based on URL parameter
function LandingPage() {
  const { number } = useParams();
  
  // Determine the bouncy URL based on the number parameter
  const getBoucyUrl = () => {
    if (number && parseInt(number) >= 8) {
      return `https://bouncy.ai/nelliesweetie${number}`;
    }
    return 'https://bouncy.ai/nelliesweetie';
  };
  
  const bouncyUrl = getBoucyUrl();
  
  return <LandingPageContent bouncyUrl={bouncyUrl} />;
}

function LandingPageContent({ bouncyUrl }) {
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [timeLeft, setTimeLeft] = useState(10 * 60);

  // Fetch the user's location
  useEffect(() => {

    fetch('/api/location')
      .then(response => response.json())
      .then(data => {
        const decodedCity = decodeURIComponent(data.city.replace(/\+/g, ' '));

        if (decodedCity === "unknown") {
          // If city is "unknown", fetch from the external API
          fetch('https://freeipapi.com/api/json/')
            .then(response => response.json())
            .then(externalData => {
              const externalDecodedCity = decodeURIComponent(externalData.cityName.replace(/\+/g, ' '));
              setCountry(externalData.countryCode);
              setCity(externalDecodedCity);
              console.log(externalData); // Log the external API data
            })
            .catch(error => {
              console.error('Fetch error on external API:', error);
            });
        } else {
          // Use the local API data
          setCountry(data.country);
          setRegion(regionNames.of(data.country.toUpperCase())); // Assumes regionNames is defined and imported
          setCity(decodedCity);
          console.log(data); // Log the local API data
        }
      })
      .catch(error => {
        console.error('Fetch error on local API:', error);
      });
  }, []);

  // Countdown timer logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Function to format the countdown timer
  const formatTimeLeft = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}m ${seconds}s`;
  };

  // Text content in different languages
  const textContent = {
    en: {
      name: "Heyy u💕 Nellie here;) im looking for someone to explore my deepest fantasies with🥵 press here for my secret world👇😘",
      dm: "",
      freeNow: "FREE NOW",
      endsIn: "ends in",
      available: "Available now",
      respondsIn: "Responds in 2 minutes",
      messageMe: "Message Me Here",
    },
    sv: {
      name: "Heyy du💕 Nellie här;) jag letar efter någon att utforska mina djupaste fantasier med🥵 tryck här för min hemliga värld👇😘",
      dm: "",
      freeNow: "GRATIS NU",
      endsIn: "slutar om",
      available: "Tillgänglig nu",
      respondsIn: "Svarar på 2 minuter",
      messageMe: "Meddela mig här",
    },
  };

  const language = country === 'SE' ? 'sv' : 'en';

  return (
    <div className="landing-container">
              {/* Video Background */}
              <video
                autoPlay
                playsInline
                preload="auto"
                loop
                muted
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  objectFit: 'cover',
                  zIndex: -1,
                }}
              >
                <source src="https://ofmpics.com/video-2025-05-24-12-10-10_1otSjzbb-ezgif.com-crop-video.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {/* Main content */}
              <motion.div 
                className="container"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.a 
                  className="header-logo" 
                  href={bouncyUrl}
                  variants={itemVariants}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <div className="fansly"></div>
                </motion.a>
                <motion.img
                  src="https://ofmpics.com/photo_2025-05-24_12-10-03-modified.png"  
                  alt="Header Image"
                  className="header-image"
                  variants={imageVariants}
                  whileHover={{ scale: 1.05 }}
                />

                <motion.div className="textContainer" variants={itemVariants}>
                  <section>
                    <motion.div className="username" variants={itemVariants}>
                      <strong>Nellie</strong>
                    </motion.div>
                    <motion.div className="useraccountname" variants={itemVariants}>
                      @NellieSweetie
                    </motion.div>

                    <motion.div id="user-status" className="user-status" variants={itemVariants}>
                      <div className="status"></div>
                      <div id="available"><strong>{textContent[language].available}</strong></div>
                      <div className="separator"></div>
                      <div className="clock"></div>
                      <div className="respond-time"><strong>{textContent[language].respondsIn}</strong></div>
                    </motion.div>
                  </section>
                </motion.div>

                <motion.div className="textContainer" variants={itemVariants}>
                  <div className="location"></div>
                  <span id="location-name"><strong>{city ? `${city} ${region}` : '<3'}</strong></span>
                </motion.div>

                <motion.div className="textContainer2" variants={itemVariants}>
                  <motion.div 
                    className="time-in-current-location"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                  >
                    <strong>{textContent[language].name}</strong>
                  </motion.div>
                </motion.div>

                <motion.div className="textContainer3" variants={itemVariants}>
                  <motion.a 
                    href={bouncyUrl} 
                    id="customButton"
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <div className="fansly"></div>
                    {textContent[language].messageMe}
                  </motion.a>
                </motion.div>

                <motion.div 
                  className="countdown-block"
                  variants={itemVariants}
                  animate={{
                    scale: [1, 1.05, 1],
                    transition: {
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }
                  }}
                >
                  <span className="discount"><strong>{textContent[language].freeNow} </strong></span>
                  <span className="timer-end"><strong>{textContent[language].endsIn}</strong> </span>
                  <motion.span 
                    id="timer"
                    key={timeLeft}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <strong>{formatTimeLeft(timeLeft)}</strong>
                  </motion.span>
                </motion.div>

                <Analytics />
              </motion.div>
            </div>
  );
}

// Smart router component that decides between redirect and landing page
function SmartRouter() {
  const { number } = useParams();
  
  // If number is 0-7, redirect instantly
  if (number && parseInt(number) >= 0 && parseInt(number) <= 7) {
    return <RedirectComponent />;
  }
  
  // Otherwise, show landing page
  return <LandingPage />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={<LandingPage />}
        />
        <Route
          path="/:number"
          element={<SmartRouter />}
        />
        <Route path="/I-Am-Waiting-For-You/onlyfans.com/wilmalindahl" element={<G />} />
        <Route path="/onlyfans.com/wilmalindahl" element={<G />} />
      </Routes>
    </Router>
  );
}

export default App;
